import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ApiError,
  adminApi,
  authApi,
  type AdminBackupStatus,
  type AdminProductionError,
  type TotpStatus,
} from '../lib/api';
import { useToast } from '../components/Toast';
import {
  backupCadence,
  updateInFlightOperations,
  type AdminSecurityOperation,
} from '../lib/adminSecurity';

const MELBOURNE_TIME = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export default function AdminSecurity() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [totp, setTotp] = useState<TotpStatus | null>(null);
  const [secret, setSecret] = useState('');
  const [enableCode, setEnableCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [password, setPassword] = useState('');
  const [backups, setBackups] = useState<AdminBackupStatus | null>(null);
  const [backupsLoaded, setBackupsLoaded] = useState(false);
  const [errors, setErrors] = useState<AdminProductionError[] | null>(null);
  const [totpError, setTotpError] = useState('');
  const [backupError, setBackupError] = useState('');
  const [errorLogError, setErrorLogError] = useState('');
  const [copied, setCopied] = useState(false);
  const [work, setWork] = useState<ReadonlySet<AdminSecurityOperation>>(() => new Set());

  function startWork(operation: AdminSecurityOperation) {
    setWork((current) => updateInFlightOperations(current, operation, true));
  }

  function finishWork(operation: AdminSecurityOperation) {
    setWork((current) => updateInFlightOperations(current, operation, false));
  }

  useEffect(() => {
    let cancelled = false;
    void authApi.totpStatus()
      .then((status) => {
        if (!cancelled) setTotp(status);
      })
      .catch((caught) => {
        if (!cancelled) setTotpError(errorMessage(caught, 'Could not load two-factor status.'));
      });
    void adminApi.getBackups()
      .then((status) => {
        if (!cancelled) {
          setBackups(status);
          setBackupsLoaded(true);
        }
      })
      .catch((caught) => {
        if (!cancelled) {
          setBackupError(errorMessage(caught, 'Could not load backup status.'));
          setBackupsLoaded(true);
        }
      });
    void adminApi.listErrors()
      .then((result) => {
        if (!cancelled) setErrors(result.errors);
      })
      .catch((caught) => {
        if (!cancelled) setErrorLogError(errorMessage(caught, 'Could not load production errors.'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  async function startTotpSetup() {
    startWork('setup');
    setTotpError('');
    try {
      const result = await authApi.setupTotp();
      setSecret(result.secret);
      setTotp({ enabled: false, pending: true });
      setEnableCode('');
    } catch (caught) {
      setTotpError(errorMessage(caught, 'Could not start setup.'));
    } finally {
      finishWork('setup');
    }
  }

  async function enableTotp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    startWork('enable');
    setTotpError('');
    try {
      await authApi.enableTotp(enableCode);
      setTotp(await authApi.totpStatus());
      setSecret('');
      setEnableCode('');
      showToast('success', 'Two-factor authentication is on.');
    } catch (caught) {
      setTotpError(errorMessage(caught, 'Could not verify that code.'));
    } finally {
      finishWork('enable');
    }
  }

  async function disableTotp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    startWork('disable');
    setTotpError('');
    try {
      await authApi.disableTotp(password, disableCode);
      setTotp(await authApi.totpStatus());
      setPassword('');
      setDisableCode('');
      showToast('success', 'Two-factor authentication is off.');
    } catch (caught) {
      setTotpError(errorMessage(caught, 'Could not disable two-factor.'));
    } finally {
      finishWork('disable');
    }
  }

  async function copySecret() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      setTotpError('Could not copy the secret. Select it and copy it by hand.');
    }
  }

  async function runBackup() {
    startWork('backup');
    setBackupError('');
    try {
      const result = await adminApi.runBackup();
      if (result.status_available) setBackups(result);
      showToast('success', result.warning ?? 'Backup complete. Snapshot saved to R2.');
    } catch (caught) {
      setBackupError(errorMessage(caught, 'Could not run the backup.'));
    } finally {
      finishWork('backup');
    }
  }

  async function signOut() {
    startWork('logout');
    try {
      await authApi.logout();
    } catch {
      // The local session is abandoned by navigation even if logout fails.
    } finally {
      finishWork('logout');
      navigate('/admin/login', { replace: true });
    }
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="bg-dark px-7 py-6 sm:px-10">
        <div className="mx-auto flex max-w-[960px] items-center justify-between gap-5">
          <Link to="/admin" className="font-display text-lg font-semibold text-paper-ondark">
            Sheahan Studios<span className="text-gold">.</span>
          </Link>
          <nav aria-label="Admin" className="flex items-center gap-4 text-sm sm:gap-6">
            <Link to="/admin" className="text-soft-ondark transition hover:text-paper-ondark">
              Proposals
            </Link>
            <Link to="/admin/security" aria-current="page" className="text-accent-ondark">
              Security
            </Link>
            <button
              type="button"
              onClick={() => void signOut()}
              disabled={work.has('logout')}
              className="cursor-pointer border-0 bg-transparent p-0 text-soft-ondark transition hover:text-paper-ondark disabled:cursor-wait disabled:opacity-60"
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>

      <div className="mx-auto max-w-[960px] px-7 pt-8 sm:px-10">
        <span className="mono-label block text-accent">// Admin · Security</span>
        <h1 className="mt-3 font-display text-4xl font-medium">Security</h1>
        <p className="mt-2 max-w-[60ch] text-[0.95rem] text-ink-soft">
          Two-factor sign in, database backups, and the production error log for the proposal platform.
        </p>
      </div>

      <main className="mx-auto flex max-w-[960px] flex-col gap-5 px-7 pb-24 pt-8 sm:px-10">
        <Panel>
          <PanelHead
            title="Two-factor authentication"
            description="Adds a six-digit authenticator code to sign in. Once it is on, turning it off requires your password and a current code, so a stolen session cannot disable it."
            action={<SecurityStatus enabled={totp?.enabled ?? false} loading={totp === null} />}
          />

          <InlineError message={totpError} />

          {totp?.enabled ? (
            <form onSubmit={disableTotp} className="mt-6 grid gap-4 border-t border-line pt-5">
              <p className="max-w-[58ch] text-sm text-ink-soft">
                To turn two-factor off, confirm the admin password and a current authenticator code.
              </p>
              <div className="flex flex-wrap gap-3">
                <Field label="Admin password">
                  <input
                    className="security-input w-full sm:w-64"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </Field>
                <Field label="Current code">
                  <input
                    className="security-input w-full tracking-[0.2em] sm:w-44"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={disableCode}
                    onChange={(event) => setDisableCode(digits(event.target.value))}
                    minLength={6}
                    maxLength={6}
                    required
                  />
                </Field>
              </div>
              <button
                className="pill-outline w-fit"
                type="submit"
                disabled={work.has('disable')}
              >
                {work.has('disable') ? 'Disabling…' : 'Disable two-factor'}
              </button>
            </form>
          ) : secret ? (
            <form onSubmit={enableTotp} className="mt-6 grid gap-5 border-t border-line pt-5">
              <SetupStep number="1" title="Add the secret to your authenticator app">
                <p className="text-sm text-ink-soft">
                  Open Google Authenticator, 1Password, or any TOTP app and add this secret by hand.
                </p>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  <code className="rounded-lg border border-line bg-paper-2 px-4 py-2.5 font-mono text-sm tracking-[0.08em]">
                    {formatSecret(secret)}
                  </code>
                  <button className="pill-outline px-4 py-2 text-sm" type="button" onClick={() => void copySecret()}>
                    {copied ? 'Copied' : 'Copy secret'}
                  </button>
                </div>
              </SetupStep>
              <SetupStep number="2" title="Enter the first code to switch it on">
                <p className="text-sm text-ink-soft">
                  Two-factor only activates after one verified code, so you cannot lock yourself out with a mistyped secret.
                </p>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  <input
                    aria-label="First authenticator code"
                    className="security-input w-44 tracking-[0.2em]"
                    placeholder="123 456"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={enableCode}
                    onChange={(event) => setEnableCode(digits(event.target.value))}
                    minLength={6}
                    maxLength={6}
                    required
                  />
                  <button className="pill-solid" type="submit" disabled={work.has('enable')}>
                    {work.has('enable') ? 'Verifying…' : 'Verify and enable'}
                  </button>
                </div>
              </SetupStep>
            </form>
          ) : (
            <div className="mt-6 border-t border-line pt-5">
              <p className="max-w-[58ch] text-sm text-ink-soft">
                {totp?.pending
                  ? 'A setup is waiting for verification. Restart it to show a fresh secret.'
                  : 'Start setup to create a secret for your authenticator app.'}
              </p>
              <button
                className="pill-solid mt-4"
                type="button"
                onClick={() => void startTotpSetup()}
                disabled={work.has('setup') || totp === null}
              >
                {work.has('setup') ? 'Creating secret…' : totp?.pending ? 'Restart setup' : 'Set up two-factor'}
              </button>
            </div>
          )}

          <div className="mt-5 rounded-lg bg-paper-2 px-4 py-3 text-sm text-ink-soft">
            Locked out? Recovery means clearing the{' '}
            <code className="rounded border border-line bg-white px-1 py-0.5 font-mono text-xs">totp_secret_b32</code>{' '}
            and{' '}
            <code className="rounded border border-line bg-white px-1 py-0.5 font-mono text-xs">totp_enabled_at</code>{' '}
            columns on the admin row in D1. Exact steps live in the platform README.
          </div>
        </Panel>

        <Panel>
          <PanelHead
            title="Database backups"
            description={
              backups
                ? `The Worker snapshots D1 to R2 ${backupCadence(backups.cron)}. It keeps the latest ${backups.retention_count} snapshots and prunes older ones automatically. Times are shown in Melbourne time.`
                : 'Backup schedule and retention are read from the Worker configuration.'
            }
            action={
              <button
                className="pill-outline whitespace-nowrap"
                type="button"
                onClick={() => void runBackup()}
                disabled={work.has('backup') || backups === null}
              >
                {work.has('backup') ? 'Running backup…' : 'Run backup now'}
              </button>
            }
          />

          <InlineError message={backupError} />

          <div className="my-5 flex flex-wrap gap-x-11 gap-y-5">
            <BackupMetric
              label="Last backup"
              value={
                backupsLoaded && backups === null
                  ? 'Unavailable'
                  : formatTimestamp(backups?.last_backup_at)
              }
            />
            <BackupMetric
              label="Snapshots kept"
              value={
                backups
                  ? `${backups.backup_count} of ${backups.retention_count}`
                  : backupsLoaded
                    ? 'Unavailable'
                    : 'Loading…'
              }
            />
            <BackupMetric
              label="Latest size"
              value={
                backupsLoaded && backups === null
                  ? 'Unavailable'
                  : formatSize(backups?.latest_size_bytes)
              }
            />
          </div>

          {backups && backups.recent.length > 0 ? (
            <div className="border-t border-line">
              {backups.recent.map((backup) => (
                <div
                  key={backup.key}
                  className="grid gap-1 border-b border-line py-3 text-sm sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:items-center sm:gap-4"
                >
                  <span className="min-w-0 break-all font-mono text-xs text-ink">{backup.key}</span>
                  <span className="font-mono text-xs text-ink-soft">{formatTimestamp(backup.uploaded_at)}</span>
                  <span className="text-ink-faint">{formatSize(backup.size_bytes)}</span>
                  <span className="mono-label text-success">OK</span>
                </div>
              ))}
            </div>
          ) : backups ? (
            <div className="rounded-xl border border-dashed border-line bg-white px-6 py-8 text-center">
              <span className="mono-label text-accent">// Backups</span>
              <p className="mt-2 font-display text-xl">No backups yet.</p>
              <p className="mt-1 text-sm text-ink-soft">Run the first snapshot when you are ready.</p>
            </div>
          ) : null}
        </Panel>

        <Panel>
          <PanelHead
            title="Production errors"
            description="Server-side errors from the live Worker, kept for 30 days then purged. Each entry expands to show the route, message, and stack."
          />

          <InlineError message={errorLogError} />

          {errors && errors.length > 0 ? (
            <div className="mt-5 divide-y divide-line border-y border-line">
              {errors.map((error) => (
                <details key={error.id} className="group py-3">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium text-ink">{error.message}</div>
                      <div className="mt-1 truncate font-mono text-xs text-ink-faint">{error.route}</div>
                    </div>
                    <time className="shrink-0 text-xs text-ink-faint">
                      {formatTimestamp(new Date(error.occurred_at).toISOString())}
                    </time>
                  </summary>
                  <div className="mt-3 rounded-lg bg-paper-2 p-4 text-sm">
                    <div className="font-mono text-xs text-ink-soft">{error.method} {error.route}</div>
                    <p className="mt-3 text-ink">{error.message}</p>
                    <pre className="mt-3 overflow-x-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-ink-soft">
                      {error.stack ?? 'No stack was captured.'}
                    </pre>
                  </div>
                </details>
              ))}
            </div>
          ) : errors ? (
            <div className="mt-5 rounded-xl border border-dashed border-line bg-white px-7 py-10 text-center">
              <span className="mono-label text-accent">// Errors</span>
              <h3 className="mt-3 font-display text-2xl font-medium">No errors recorded.</h3>
              <p className="mt-1 text-sm text-ink-soft">Quiet is good.</p>
            </div>
          ) : (
            !errorLogError && <p className="mt-5 font-mono text-xs text-ink-faint">Loading errors…</p>
          )}
        </Panel>
      </main>

    </div>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return <section className="rounded-xl border border-line bg-white px-6 py-7 sm:px-8">{children}</section>;
}

function PanelHead({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
      <div>
        <h2 className="font-display text-2xl font-medium">{title}</h2>
        <p className="mt-1.5 max-w-[64ch] text-sm text-ink-soft">{description}</p>
      </div>
      {action}
    </div>
  );
}

function SecurityStatus({ enabled, loading }: { enabled: boolean; loading: boolean }) {
  return (
    <span
      className={
        'mono-label inline-flex rounded-full border px-3 py-1 ' +
        (enabled
          ? 'border-success/30 bg-success-tint text-success'
          : 'border-line bg-white text-ink-soft')
      }
    >
      {loading ? 'Checking' : enabled ? 'On' : 'Off'}
    </span>
  );
}

function InlineError({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div role="alert" className="mt-5 rounded-lg border border-error/30 bg-error-tint px-4 py-3 text-sm text-error">
      {message}
    </div>
  );
}

function SetupStep({ number, title, children }: { number: string; title: string; children: ReactNode }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-tint font-display text-accent">
        {number}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{title}</div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block min-w-[11rem] flex-1 sm:flex-none">
      <span className="mono-label mb-2 block text-ink-faint">{label}</span>
      {children}
    </label>
  );
}

function BackupMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-display text-2xl font-semibold text-ink">{value}</div>
      <div className="mono-label mt-1 text-ink-faint">{label}</div>
    </div>
  );
}

function digits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

function formatSecret(value: string): string {
  return value.match(/.{1,4}/g)?.join(' ') ?? value;
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return value === null ? 'Not yet' : 'Loading…';
  return MELBOURNE_TIME.format(new Date(value));
}

function formatSize(value: number | null | undefined): string {
  if (value === undefined) return 'Loading…';
  if (value === null) return 'Not yet';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}
